import React, { useState, useEffect } from 'react';

import Select from '~/components/Select';
import DistributedColumnsGeneral1 from '~/components/StatisticsCharts/DistributedColumnsGeneral1';
import SimpleDonutGeneral from '~/components/StatisticsCharts/SimpleDonutGeneral';

import { getRepValueForSelect, getClientValueForSelect } from '~/utils';

import { useAuth } from '~/hooks/auth';

import api from '~/services/api';

import { STATE_OPTIONS } from '../../../constants';

interface IFilters {
  state: string;
  state_refreshed: boolean;
  erp_rep_code: string;
  rep_refreshed: boolean;
  erp_client_code: string;
  status: string;
  year: string;
  refresh: boolean;
  refresh_projects: boolean;
}

const General: React.FC = () => {
  const { user } = useAuth();
  const [representatives, setRepresentatives] = useState<
    {
      code: string;
      name: string;
    }[]
  >();
  const [clients, setClients] = useState<
    {
      code: string;
      company_name: string;
    }[]
  >();
  const [projects, setProjects] = useState<
    {
      type: string;
    }[]
  >([]);
  const [filters, setFilters] = useState<IFilters>({
    state: 'ALL',
    state_refreshed: true,
    erp_rep_code: user.user_type.type === 'REP' ? user.erp_rep_code : 'ALL',
    rep_refreshed: true,
    erp_client_code: 'ALL',
    status: 'ALL',
    year: 'ALL',
    refresh: true,
    refresh_projects: false,
  });
  const [totals, setTotals] = useState<{
    display_projects: number;
    material_projects: number;
    sponsorship_projects: number;
    adversity_projects: number;
    folder_projects: number;
    sample_projects: number;
    event_projects: number;
    rebate_projects: number;
  }>({
    display_projects: 0,
    material_projects: 0,
    sponsorship_projects: 0,
    adversity_projects: 0,
    folder_projects: 0,
    sample_projects: 0,
    event_projects: 0,
    rebate_projects: 0,
  });

  useEffect(() => {
    const refreshFilters = async () => {
      let { erp_rep_code, erp_client_code, rep_refreshed } = filters;

      if (filters.state_refreshed) {
        await api
          .get(`/localrepresentatives/state?state=${filters.state}`)
          .then(response => {
            setRepresentatives([
              {
                code: 'ALL',
                name: '',
              },
              ...response.data,
            ]);

            erp_rep_code = 'ALL';
            rep_refreshed = true;
          });
      }

      if (rep_refreshed) {
        erp_rep_code =
          user.user_type.type === 'REP' ? user.erp_rep_code : erp_rep_code;

        await api
          .get(`/localclients/rep?rep_code=${erp_rep_code}`)
          .then(response => {
            setClients([
              {
                code: 'ALL',
                company_name: '',
              },
              ...response.data,
            ]);

            erp_client_code = 'ALL';
          });
      }

      setFilters({
        ...filters,
        state_refreshed: false,
        erp_rep_code,
        rep_refreshed: false,
        erp_client_code,
        refresh: false,
        refresh_projects: true,
      });
    };

    if (filters.refresh) {
      refreshFilters();
    }
  }, [filters, user]);

  useEffect(() => {
    const loadProjects = async () => {
      await api
        .get(
          `/projects/projectstatistics?` +
            `state=${filters.state}&` +
            `erp_rep_code=${filters.erp_rep_code}&` +
            `erp_client_code=${filters.erp_client_code}&` +
            `status=${filters.status}&` +
            `year=${filters.year}`,
        )
        .then(response => {
          setProjects(response.data);

          setFilters({ ...filters, refresh_projects: false });
        });
    };

    if (filters.refresh_projects) {
      loadProjects();
    }
  }, [filters]);

  useEffect(() => {
    setTotals({
      display_projects: projects.filter(project => project.type === 'DISPLAY')
        .length,
      material_projects: projects.filter(project => project.type === 'MATERIAL')
        .length,
      sponsorship_projects: projects.filter(
        project => project.type === 'PATROCINIO',
      ).length,
      adversity_projects: projects.filter(
        project => project.type === 'ADVERSIDADE',
      ).length,
      folder_projects: projects.filter(project => project.type === 'PASTA')
        .length,
      sample_projects: projects.filter(project => project.type === 'AMOSTRA')
        .length,
      event_projects: projects.filter(project => project.type === 'EVENTO')
        .length,
      rebate_projects: projects.filter(project => project.type === 'REBATE')
        .length,
    });
  }, [projects]);

  const handleFilter = (): React.ReactNode => {
    if (user.user_type.type !== 'REP') {
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ width: 250 }}>
            <Select
              label="Estado"
              options={STATE_OPTIONS}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  state: e.value,
                  state_refreshed: true,
                  refresh: true,
                });
              }}
            />
          </div>

          <div style={{ width: 250 }}>
            <Select
              label="Representante"
              options={representatives?.map(representative => ({
                value: representative.code,
                label:
                  representative.code === 'ALL'
                    ? 'Todos'
                    : `(${representative.code}) - ${representative.name}`,
              }))}
              value={getRepValueForSelect(
                filters.erp_rep_code,
                representatives,
              )}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  erp_rep_code: e.value,
                  rep_refreshed: true,
                  refresh: true,
                });
              }}
            />
          </div>

          <div style={{ width: 250 }}>
            <Select
              label="Cliente"
              options={clients?.map(client => ({
                value: client.code,
                label:
                  client.code === 'ALL'
                    ? 'Todos'
                    : `(${client.code}) - ${client.company_name}`,
              }))}
              value={getClientValueForSelect(filters.erp_client_code, clients)}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  erp_client_code: e.value,
                  refresh: true,
                });
              }}
            />
          </div>

          <div style={{ width: 250 }}>
            <Select
              label="Status"
              options={[
                { value: 'Pendente', label: 'Pendente' },
                { value: 'Finalizado', label: 'Finalizado' },
                { value: 'Cancelado', label: 'Cancelado' },
                { value: 'ALL', label: 'Todos' },
              ]}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  status: e.value,
                  refresh: true,
                });
              }}
            />
          </div>

          <div style={{ width: 250 }}>
            <Select
              label="Ano"
              options={[
                { value: 'ALL', label: 'Todos' },
                ...Array.from(
                  { length: new Date().getFullYear() - 2021 + 1 },
                  (_, i) => new Date().getFullYear() - i,
                ).map(y => ({ value: String(y), label: String(y) })),
              ]}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  year: e.value,
                  refresh: true,
                });
              }}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div style={{ maxWidth: 1080 }}>
      {handleFilter()}

      <div
        style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 20 }}
      >
        <div style={{ width: 450 }}>
          <SimpleDonutGeneral
            displayProjectsAmount={totals.display_projects}
            materialProjectsAmount={totals.material_projects}
            sponsorshipProjectsAmount={totals.sponsorship_projects}
            adversityProjectsAmount={totals.adversity_projects}
            folderProjectsAmount={totals.folder_projects}
            sampleProjectsAmount={totals.sample_projects}
            eventProjectsAmount={totals.event_projects}
            rebateProjectsAmount={totals.rebate_projects}
          />
        </div>

        <div style={{ width: 500 }}>
          <DistributedColumnsGeneral1
            displayProjectsAmount={totals.display_projects}
            materialProjectsAmount={totals.material_projects}
            sponsorshipProjectsAmount={totals.sponsorship_projects}
            adversityProjectsAmount={totals.adversity_projects}
            folderProjectsAmount={totals.folder_projects}
            sampleProjectsAmount={totals.sample_projects}
            eventProjectsAmount={totals.event_projects}
            rebateProjectsAmount={totals.rebate_projects}
          />
        </div>
      </div>
    </div>
  );
};

export default General;
